import { useMemo, useCallback } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';

/**
 * Parse project info markdown content into sections
 * The backend generates content with:
 * - `#` (H1) for main sections (Executive Summary, Project Vision, etc.)
 * - `##` (H2) for subsections within those sections
 *
 * This parser treats H1 (#) as accordion sections since that's how the backend structures it.
 *
 * @param {string} content - The markdown content to parse
 * @returns {Array} - Array of parsed sections
 */
export const parseProjectInfoSections = (content) => {
  if (!content) return [];

  // Handle escaped newlines by replacing \\n with actual newlines
  const normalizedContent = content.replace(/\\n/g, '\n');

  const lines = normalizedContent.split('\n');
  const sections = [];
  let currentSection = null;
  let currentContent = [];
  let inTableOfContents = false;
  let sectionIndex = 0; // Track section index for unique IDs

  // Skip titles that are document headers, not content sections
  const skipTitles = [
    'PROJECT INFORMATION DOCUMENT',
    'PROJECT INFORMATION',
    'PROJECT INFO',
    'TABLE OF CONTENTS',
    'CONTENTS',
    'TOC'
  ];

  lines.forEach((line) => {
    // Match # for main sections (H1) - these become accordions
    const singleHashMatch = line.match(/^#\s+(.+)$/);
    // Match ## for subsections (H2) - these stay as content within the section
    const doubleHashMatch = line.match(/^##\s+(.+)$/);
    // Match ### for sub-subsections (H3) - also stay as content
    const tripleHashMatch = line.match(/^###\s+(.+)$/);

    // H1 - Main section headers become accordion sections
    if (singleHashMatch && !doubleHashMatch) {
      const title = singleHashMatch[1].trim();
      const upperTitle = title.toUpperCase();

      // Check if this is a document header or TOC to skip
      if (skipTitles.some(skip => upperTitle.includes(skip))) {
        inTableOfContents = upperTitle.includes('TABLE OF CONTENTS') ||
                           upperTitle.includes('CONTENTS') ||
                           upperTitle === 'TOC';
        return;
      }

      // Save previous section if exists
      if (currentSection && !inTableOfContents) {
        // Generate unique ID by appending index - handles duplicate titles
        const baseId = currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        sections.push({
          id: `${baseId}-${sectionIndex}`,
          title: currentSection,
          content: currentContent.join('\n').trim(),
          category: null,
          isFirstInCategory: false
        });
        sectionIndex++;
      }

      // Start new section
      inTableOfContents = false;
      currentSection = title;
      currentContent = [];
    } else if (doubleHashMatch || tripleHashMatch) {
      // H2 and H3 are subsections - include them as content with their headers
      if (!inTableOfContents && currentSection) {
        currentContent.push(line);
      }
    } else {
      // Regular content lines
      if (!inTableOfContents && currentSection) {
        currentContent.push(line);
      }
    }
  });

  // Add the last section
  if (currentSection && !inTableOfContents) {
    // Generate unique ID by appending index - handles duplicate titles
    const baseId = currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    sections.push({
      id: `${baseId}-${sectionIndex}`,
      title: currentSection,
      content: currentContent.join('\n').trim(),
      category: null,
      isFirstInCategory: false
    });
  }

  // If only 0 or 1 H1 section found, try parsing with ## or ### as sections (fallback for different formats)
  // This handles documents that use # for title and ## for actual sections
  if (sections.length <= 1) {
    const fallbackSections = parseProjectInfoSectionsFallback(normalizedContent);

    // If fallback gives us more sections, use it
    if (fallbackSections.length > sections.length) {
      return fallbackSections;
    }

    // If fallback also failed, try bold text parser
    if (sections.length === 0) {
      const boldSections = parseProjectInfoBoldTextSections(normalizedContent);
      return boldSections;
    }
  }

  return sections;
};

/**
 * Parser for content that uses bold text (**Title:**) as section headers
 * Common in AI conversational responses
 */
const parseProjectInfoBoldTextSections = (content) => {
  if (!content) return [];

  const lines = content.split('\n');
  const sections = [];
  let currentSection = null;
  let currentContent = [];
  let sectionIndex = 0; // Track section index for unique IDs

  // Known section title patterns to look for (case-insensitive)
  // Extended list to handle various AI output formats
  const sectionPatterns = [
    // Standard project info sections
    'Executive Summary', 'Project Vision', 'Project Overview', 'Business Case',
    'Target Audience', 'User Personas', 'Competitive Analysis', 'Market Analysis',
    'Success Metrics', 'KPIs', 'Key Performance Indicators', 'Project Scope',
    'Features', 'Core Features', 'Key Features', 'Functional Requirements',
    'Non-Functional Requirements', 'Technical Requirements', 'Architecture',
    'Tech Stack', 'Technology Stack', 'Timeline', 'Milestones', 'Roadmap',
    'Risk Assessment', 'Risks', 'Constraints', 'Assumptions', 'Dependencies',
    'Stakeholders', 'Team', 'Resources', 'Budget', 'Cost Estimation',
    'Conclusion', 'Summary', 'Next Steps', 'Recommendations',
    'Introduction', 'Background', 'Problem Statement', 'Solution Overview',
    'User Stories', 'Use Cases', 'Acceptance Criteria', 'Testing Strategy',
    'Deployment', 'Maintenance', 'Support', 'Documentation',
    'Key Success Factors', 'Expected Outcomes', 'Goals', 'Objectives',
    // Risk-related sections
    'Risk Considerations', 'Risk Analysis', 'Risk Mitigation', 'High-Risk Areas',
    'Mitigation Strategies', 'Risk Factors',
    // Implementation sections
    'Implementation Plan', 'Implementation Strategy', 'Development Plan',
    'Technical Implementation', 'System Design', 'System Architecture',
    // Additional common sections
    'Overview', 'Vision', 'Mission', 'Scope', 'Deliverables',
    'Requirements', 'Specifications', 'Design', 'Approach', 'Methodology',
    'Key Points', 'Highlights', 'Action Items', 'To Do', 'Tasks'
  ];

  lines.forEach((line) => {
    // Match various bold text header formats:
    // - **Title:** or **Title**
    // - 1. **Title** or 1. **Title** - Description
    // - **Title:** Some content
    const boldMatch = line.match(/^\*\*([^*]+)\*\*:?\s*$/);
    const boldWithContentMatch = line.match(/^\*\*([^*]+)\*\*:\s*(.+)$/);
    const numberedBoldMatch = line.match(/^\d+\.\s*\*\*([^*]+)\*\*\s*(?:-.*)?$/);
    const numberedBoldWithDescMatch = line.match(/^\d+\.\s*\*\*([^*]+)\*\*\s*-\s*(.+)$/);

    const matchFound = boldMatch || boldWithContentMatch || numberedBoldMatch || numberedBoldWithDescMatch;

    if (matchFound) {
      // Extract title from whichever pattern matched
      let title = '';
      let inlineContent = '';

      if (numberedBoldWithDescMatch) {
        title = numberedBoldWithDescMatch[1].trim();
        inlineContent = numberedBoldWithDescMatch[2].trim();
      } else if (numberedBoldMatch) {
        title = numberedBoldMatch[1].trim();
      } else if (boldWithContentMatch) {
        title = boldWithContentMatch[1].trim();
        inlineContent = boldWithContentMatch[2].trim();
      } else if (boldMatch) {
        title = boldMatch[1].trim();
      }

      // Check if this looks like a section header
      // More permissive: matches known patterns, is title-case, or is a short capitalized phrase
      const isLikelySection = sectionPatterns.some(pattern =>
        title.toLowerCase().includes(pattern.toLowerCase())
      ) || /^[A-Z][a-zA-Z0-9\s&\-_]+$/.test(title) || // Title case with hyphens/underscores
         (title.length <= 50 && title.length >= 2 && /^[A-Z]/.test(title)); // Any short capitalized phrase

      if (isLikelySection) {
        // Save previous section if exists
        if (currentSection && currentContent.length > 0) {
          // Generate unique ID by appending index - handles duplicate titles
          const baseId = currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-');
          sections.push({
            id: `${baseId}-${sectionIndex}`,
            title: currentSection,
            content: currentContent.join('\n').trim(),
            category: null,
            isFirstInCategory: false
          });
          sectionIndex++;
        }

        currentSection = title;
        currentContent = [];

        // If header had inline content/description, add it
        if (inlineContent) {
          currentContent.push(inlineContent);
        }
      } else if (currentSection) {
        // Not a section header, treat as content
        currentContent.push(line);
      }
    } else if (currentSection) {
      // Regular content line
      currentContent.push(line);
    }
  });

  // Add last section
  if (currentSection && currentContent.length > 0) {
    // Generate unique ID by appending index - handles duplicate titles
    const baseId = currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    sections.push({
      id: `${baseId}-${sectionIndex}`,
      title: currentSection,
      content: currentContent.join('\n').trim(),
      category: null,
      isFirstInCategory: false
    });
  }

  // DO NOT create a single "Project Information" fallback section
  // If no proper sections found, return empty array to prevent version creation
  // This prevents conversational AI responses from creating incorrect versions

  return sections;
};

/**
 * Fallback parser for content that uses ## or ### for sections instead of #
 * Handles multiple formats:
 * - ## Category / ### Section (nested structure)
 * - ## Section (flat structure - treats ## as sections directly)
 */
const parseProjectInfoSectionsFallback = (content) => {
  if (!content) return [];

  const lines = content.split('\n');

  // First pass: check if there are any ### headers
  const hasTripleHashHeaders = lines.some(line => /^###\s+.+$/.test(line));

  const sections = [];
  let currentCategory = null;
  let currentSection = null;
  let currentContent = [];
  let sectionIndex = 0; // Track section index for unique IDs

  lines.forEach((line) => {
    const doubleHashMatch = line.match(/^##\s+(.+)$/);
    const tripleHashMatch = line.match(/^###\s+(.+)$/);

    if (doubleHashMatch && !tripleHashMatch) {
      // Save previous section if exists
      if (currentSection) {
        // Generate unique ID by appending index - handles duplicate titles like "Goal"
        const baseId = currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        sections.push({
          id: `${baseId}-${sectionIndex}`,
          title: currentSection,
          content: currentContent.join('\n').trim(),
          category: currentCategory,
          isFirstInCategory: false
        });
        sectionIndex++;
      }

      const headerTitle = doubleHashMatch[1].trim();

      if (hasTripleHashHeaders) {
        // If document has ### headers, treat ## as category only
        currentCategory = headerTitle;
        currentSection = null;
        currentContent = [];
      } else {
        // If no ### headers exist, treat ## as section headers directly
        currentSection = headerTitle;
        currentCategory = null;
        currentContent = [];
      }
    } else if (tripleHashMatch) {
      // ### is always a section
      if (currentSection) {
        // Generate unique ID by appending index - handles duplicate titles like "Goal"
        const baseId = currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        sections.push({
          id: `${baseId}-${sectionIndex}`,
          title: currentSection,
          content: currentContent.join('\n').trim(),
          category: currentCategory,
          isFirstInCategory: false
        });
        sectionIndex++;
      }
      let sectionTitle = tripleHashMatch[1].trim();
      sectionTitle = sectionTitle.replace(/^\d+(\.\d+)*\.?\s*/, '');
      currentSection = sectionTitle;
      currentContent = [];
    } else if (currentSection) {
      // Collect content when we have a current section
      currentContent.push(line);
    } else if (!hasTripleHashHeaders && currentCategory) {
      // For flat ## structure without ###, collect content under category
      // This handles edge case where ## is treated as category but content follows
      currentContent.push(line);
    }
  });

  // Add last section
  if (currentSection) {
    // Generate unique ID by appending index - handles duplicate titles like "Goal"
    const baseId = currentSection.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    sections.push({
      id: `${baseId}-${sectionIndex}`,
      title: currentSection,
      content: currentContent.join('\n').trim(),
      category: currentCategory,
      isFirstInCategory: false
    });
  }

  // Mark first section in each category
  let lastCategory = null;
  sections.forEach(section => {
    if (section.category !== lastCategory && section.category !== null) {
      section.isFirstInCategory = true;
      lastCategory = section.category;
    }
  });

  // DO NOT create a single "Project Information" fallback section
  // If no proper sections found, return empty array to prevent version creation
  // This prevents conversational AI responses from creating incorrect versions

  return sections;
};

/**
 * Check if content has valid project info structure
 * Must be a FULL project info document with proper structure, not a conversational response
 *
 * BALANCED requirements:
 * - Proper markdown headers (H1 #, H2 ##) are strong indicators → more lenient thresholds
 * - Bold text headers are weaker indicators → stricter thresholds (could be conversational)
 * - Minimum content length of 300 characters
 */
const hasValidProjectInfoStructure = (content) => {
  if (!content) return false;

  // Minimum content length check - filters out very short responses
  // Reduced from 500 to 300 to allow smaller but valid documents
  if (content.length < 300) return false;

  // Count # headers (H1 sections) - but not ## or ###
  const h1Matches = content.match(/^#\s+[^#].+$/gm);
  const h1Count = h1Matches ? h1Matches.length : 0;

  // Count ## headers (H2 sections) - but not ###
  const h2Matches = content.match(/^##\s+[^#].+$/gm);
  const h2Count = h2Matches ? h2Matches.length : 0;

  // Count ### headers (H3 sections)
  const h3Matches = content.match(/^###\s+.+$/gm);
  const h3Count = h3Matches ? h3Matches.length : 0;

  // Count bold text headers (**Title:** or **Title**)
  const boldMatches = content.match(/^\*\*[^*]+\*\*:?\s*$/gm);
  const boldCount = boldMatches ? boldMatches.length : 0;

  // Count numbered bold headers (1. **Title** or 1. **Title** - Description)
  const numberedBoldMatches = content.match(/^\d+\.\s*\*\*[^*]+\*\*/gm);
  const numberedBoldCount = numberedBoldMatches ? numberedBoldMatches.length : 0;

  // Project info keywords - expanded list for flexibility
  const projectInfoKeywords = [
    // Canonical sections
    'Executive Summary', 'Project Vision', 'Business Case', 'Target Audience',
    'Project Scope', 'Constraints & Assumptions', 'Constraints', 'Assumptions',
    'Success Metrics', 'Competitive Analysis', 'Risk Assessment', 'Timeline',
    'Milestones', 'Project Overview', 'Objectives', 'Stakeholders',
    // Common variations
    'Overview', 'Summary', 'Vision', 'Goals', 'Requirements', 'Features',
    'Scope', 'Risks', 'Dependencies', 'Architecture', 'Technical',
    'Implementation', 'Deliverables', 'Budget', 'Resources', 'Team',
    'Schedule', 'Introduction', 'Background', 'Problem Statement', 'Solution'
  ];

  const keywordMatches = projectInfoKeywords.filter(keyword =>
    content.toLowerCase().includes(keyword.toLowerCase())
  );

  // TIERED VALIDATION based on header type:
  // Proper markdown headers (H1, H2) are strong indicators of structured documents
  // Bold text could be from conversational responses, so require stricter checks

  // Tier 1: H1 headers (strongest indicator) - require only 2+ sections
  // Imported .md files typically use # for sections
  if (h1Count >= 2) {
    return true; // Trust proper H1 markdown structure
  }

  // Tier 2: H2 headers (strong indicator) - require 2+ sections
  if (h2Count >= 2) {
    return true; // Trust proper H2 markdown structure
  }

  // Tier 3: H3 headers (moderate indicator) - require 3+ sections
  if (h3Count >= 3) {
    return true;
  }

  // Tier 4: Bold text headers (weak indicator - could be conversational)
  // Require MORE sections AND keywords to filter out conversational responses
  if (boldCount >= 4 || numberedBoldCount >= 4) {
    // For bold headers, also require at least 2 project keywords
    // This filters out conversational responses like "Here's what I'll add: **Kanban**..."
    return keywordMatches.length >= 2;
  }

  // Tier 5: Mixed or minimal structure - require keywords to validate
  const totalSections = h1Count + h2Count + h3Count + boldCount + numberedBoldCount;
  if (totalSections >= 3 && keywordMatches.length >= 2) {
    return true;
  }

  // Content doesn't have enough structure to be a project info document
  return false;
};

/**
 * Custom hook to manage project info versions
 * Uses useVersionManagementV1 for consistent version handling across all tasks
 */
const useProjectInfoVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // IMPORTANT: Memoize config functions to prevent unnecessary effect re-runs
  // These functions are in the dependency array of useVersionManagementV1's main effect
  // Without memoization, new function instances are created on every render,
  // causing the effect to run repeatedly and potentially create duplicate versions

  // Determine if a message contains valid project info content
  const isRelevantMessage = useCallback((msg) => {
    const content = msg.content || '';
    return hasValidProjectInfoStructure(content);
  }, []);

  // Parse project info sections from content — prefer backend parsed_content
  const parseContent = useCallback((content, msg) => {
    if (msg?.parsed_content?.sections?.length) {
      return msg.parsed_content.sections;
    }
    return parseProjectInfoSections(content);
  }, []);

  // Extract version information from a message
  const extractVersions = useCallback((msg, parsedSections, versionNumber, isPreview = false) => {
    return {
      id: null, // Will be overwritten by useVersionManagement with messageIndex-based ID
      timestamp: msg.createdAt || new Date().toISOString(),
      sections: parsedSections,
      content: msg.content,
      versionNumber: versionNumber,
      isPreview: isPreview,
      label: isPreview ? 'Generating...' : undefined
    };
  }, []);

  // Configuration for project info version management
  const projectInfoConfig = useMemo(() => ({
    isRelevantMessage,
    parseContent,
    extractVersions,
    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  }), [isRelevantMessage, parseContent, extractVersions, conversation, isStreaming, streamingContent, isLocalUpdateRef]);

  // Use the version management hook
  const versionData = useVersionManagementV1(projectInfoConfig);

  // Get currentVersion directly based on index - no memoization to avoid stale data
  // This ensures we always get the correct version when index changes
  const currentVersion = useMemo(() => {
    const version = versionData.versions[versionData.currentVersionIndex];
    if (!version) return null;
    return {
      ...version,
      sections: version.parsedContent || version.sections
    };
  }, [versionData.versions, versionData.currentVersionIndex]);

  // Map the generic version data to project info specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useProjectInfoVersions;
